import { isLoggedIn, LogSentryException, ServiceError } from "@shapr3d/frontend-lib";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { SyncRequestClient } from "../stores/requestClient";
import type { Brand } from "../utils/typeUtils";
import type { UserID } from "./useAccount";
import type { DrawingID } from "./useComments";
import { useProjectViewLinkInvitation } from "./useProjectViewLink";

export const webExportQueryKey = ["webExportQueryKey"];

export type TeamID = Brand<string, "TeamID">;
export type WebExportID = Brand<string, "WebExportID">;

export type WebExportSharedState = "not_shared" | "anyone_with_link" | "team_only" | "restricted";
export type WebExportUserRole = "owner" | "viewer" | "commenter";
export type WebExportBaseRole = "viewer" | "commenter";

export const WebExportUserRolesDisplayNames: { [key in WebExportUserRole]: string } = {
  owner: "Owner",
  viewer: "Viewer",
  commenter: "Commenter",
} as const;

export const WebExportSharedStates = {
  NotShared: "not_shared",
  AnyoneWithLink: "anyone_with_link",
  TeamOnly: "team_only",
  Restricted: "restricted",
} as const satisfies { [key in string]: WebExportSharedState };

export const WebExportBaseRoles = {
  Viewer: "viewer",
  Commenter: "commenter",
} as const satisfies { [key in string]: WebExportBaseRole };

export type HTTPMethod = "GET" | "POST" | "PUT" | "DELETE";

export type FileDownload = {
  method: HTTPMethod;
  url: string;
  headers?: Record<string, string>;
  expiry: string;
};

export type Drawing = {
  drawingId: DrawingID;
  fileType: "pdf_v1" | "svg_v1";
  title: string;
  download: FileDownload;
};

export type WebExport = {
  webExportId: WebExportID;
  projectId: string;
  revisionId: number;
  schemaVersion: number;
  uploadedByUserId: UserID;
  uploadedByDeviceId: string;
  uploadedByPlatform: string;
  appVersion: string;
  fileSize: number;
  createdAt: string;
  uploadedAt?: string;
  title: string;
  includesMaterials: boolean;

  modelFileDownload?: FileDownload;
  drawingsDownload?: Drawing[];
};

export type WebExportResponseData = {
  webExport: WebExport;
  teamId: TeamID;
  sharedState: WebExportSharedState;
  baseRole: WebExportBaseRole;
  userRole: WebExportUserRole;
};

export type WebExportData = WebExportResponseData & {
  isCommentingEnabled: boolean;
};

export type WebExportRequestPayload = object;

const getIsCommentingEnabled = ({ userRole }: { userRole: WebExportUserRole }) => {
  const allowedRoles: WebExportUserRole[] = ["owner", "commenter"];

  return allowedRoles.includes(userRole);
};

export const useWebExport = () => {
  const { viewLink } = useParams();

  const { enabled: isProjectViewLinkInvitationEnabled } = useProjectViewLinkInvitation();

  const body: WebExportRequestPayload = {};

  return useQuery<WebExportData>({
    queryKey: [...webExportQueryKey, viewLink],
    queryFn: async ({ signal }) => {
      if (isLoggedIn()) {
        try {
          const response = await SyncRequestClient.authenticatedRequest({
            method: "POST",
            url: `/web/web-exports?v=${viewLink}`,
            body,
            abortSignal: signal,
          });
          const json = await response.json<WebExportResponseData>();
          return {
            ...json,
            isCommentingEnabled: getIsCommentingEnabled({
              userRole: json.userRole,
            }),
          };
        } catch (error) {
          if (error instanceof ServiceError) {
            throw error;
          }
          LogSentryException({
            messagePrefix: "Web export download error (authenticated)",
            error,
          });
          throw error;
        }
      }

      try {
        const response = await SyncRequestClient.publicRequest({
          method: "POST",
          url: `/web/web-exports?v=${viewLink}`,
          body,
          abortSignal: signal,
        });
        const json = await response.json<WebExportResponseData>();

        return {
          ...json,
          isCommentingEnabled: getIsCommentingEnabled({
            userRole: json.userRole,
          }),
        };
      } catch (error) {
        if (!(error instanceof ServiceError)) {
          LogSentryException({ messagePrefix: "Web export download error (public)", error });
        }

        throw error;
      }
    },
    enabled: Boolean(viewLink) && !isProjectViewLinkInvitationEnabled,
  });
};
